import { BaseClient } from "../../clients/base/BaseClient";
import { SERVICE_ENDPOINTS } from "./service.consts";
import type {
  DatabaseVersionResponse,
  DatabaseServerVersionResponse,
  ServiceInfoResponse,
  AdapterInfoResponse,
  LogoResponse,
  LogoFormat,
  ClearCacheResponse,
} from "./service.types";

/**
 * Service API Client
 * Handles all service information and observability operations.
 */
export class ServiceClient extends BaseClient {
  /**
   * Fetch internal database version.
   * @param {AbortSignal | null} signal - Optional abort signal for request cancellation.
   * @returns {Promise<DatabaseVersionResponse>} Database migration version string.
   */
  async fetchDatabaseVersion(signal?: AbortSignal | null): Promise<DatabaseVersionResponse> {
    return this.get<DatabaseVersionResponse>(SERVICE_ENDPOINTS.DB_VERSION, {
      signal,
    });
  }

  /**
   * Fetch database server version.
   * @param {AbortSignal | null} signal - Optional abort signal for request cancellation.
   * @returns {Promise<DatabaseServerVersionResponse>} PostgreSQL version string.
   */
  async fetchDatabaseServerVersion(
    signal?: AbortSignal | null,
  ): Promise<DatabaseServerVersionResponse> {
    return this.get<DatabaseServerVersionResponse>(SERVICE_ENDPOINTS.DB_SERVER_VERSION, {
      signal,
    });
  }

  /**
   * Fetch Core application information.
   * @param {AbortSignal | null} signal - Optional abort signal for request cancellation.
   * @returns {Promise<ServiceInfoResponse>} Core application info.
   */
  async fetchServiceInfo(signal?: AbortSignal | null): Promise<ServiceInfoResponse> {
    return this.get<ServiceInfoResponse>(SERVICE_ENDPOINTS.INFO, {
      signal,
    });
  }

  /**
   * Fetch Adapter application information.
   * @param {AbortSignal | null} signal - Optional abort signal for request cancellation.
   * @returns {Promise<AdapterInfoResponse>} Adapter application info.
   */
  async fetchAdapterInfo(signal?: AbortSignal | null): Promise<AdapterInfoResponse> {
    return this.get<AdapterInfoResponse>(SERVICE_ENDPOINTS.ADAPTER_INFO, {
      signal,
    });
  }

  /**
   * Fetch application logo (default SVG format).
   * @param {AbortSignal | null} signal - Optional abort signal for request cancellation.
   * @returns {Promise<LogoResponse>} Logo image as Blob.
   */
  async fetchLogo(signal?: AbortSignal | null): Promise<LogoResponse> {
    return this.get<LogoResponse>(SERVICE_ENDPOINTS.LOGO, {
      signal,
    });
  }

  /**
   * Fetch application logo in specific format.
   * @param {LogoFormat} format - Logo format (svg or png).
   * @param {AbortSignal | null} signal - Optional abort signal for request cancellation.
   * @returns {Promise<LogoResponse>} Logo image as Blob.
   */
  async fetchLogoByFormat(format: LogoFormat, signal?: AbortSignal | null): Promise<LogoResponse> {
    return this.get<LogoResponse>(SERVICE_ENDPOINTS.LOGO_FORMAT(format), {
      signal,
    });
  }

  /**
   * Clear Core cache.
   * @returns {Promise<ClearCacheResponse>} Cache clear result or empty response.
   */
  async clearCoreCache(): Promise<ClearCacheResponse> {
    return this.delete<ClearCacheResponse>(SERVICE_ENDPOINTS.CACHE);
  }
}
